
// API call to TeamGPT through our Netlify function proxy
export const callTeamGptApi = async (message: string, apiKey: string, organizationId?: string) => {
  try {
    console.log("Attempting to call TeamGPT API with message:", message);
    console.log("Using API key (first 10 chars):", apiKey.substring(0, 10) + "...");
    if (organizationId) {
      console.log("Using organization ID:", organizationId);
    }
    
    // Determine if we're in development or production
    const isLocalDevelopment = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
    
    // Set the proxy endpoint URL based on environment
    let proxyEndpoint;
    if (isLocalDevelopment) {
      // When running netlify dev locally
      proxyEndpoint = "/.netlify/functions/teamgpt-proxy";
    } else {
      // When deployed to Netlify
      proxyEndpoint = "/api/teamgpt-proxy";
    }
    
    console.log(`Using proxy endpoint: ${proxyEndpoint}`);
    console.log(`Full request URL: ${window.location.origin}${proxyEndpoint}`);
    
    // Prepare request data for logging
    const requestData = {
      message,
      apiKey: apiKey.substring(0, 10) + "...", // Redacted for logging
      organizationId
    };
    console.log("Request data being sent to proxy:", JSON.stringify(requestData, null, 2));
    
    // Make the request to our Netlify function
    const response = await fetch(proxyEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        message,
        apiKey,
        organizationId
      })
    });
    
    console.log("Proxy response status:", response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error("Proxy error response:", errorText);
      
      try {
        // Try to parse error as JSON for better logging
        const errorJson = JSON.parse(errorText);
        console.error("Parsed error details:", errorJson);
      } catch (e) {
        // If not JSON, log as is
        console.error("Error response is not JSON");
      }
      
      // Add detailed error information for debugging
      if (response.status === 404) {
        console.error("404 Not Found: The Netlify function could not be found. Please check if it's deployed correctly.");
        return `I'm having trouble connecting to the TeamGPT API via the Netlify function. It seems the function hasn't been deployed correctly or isn't accessible (404 error).

To fix this:
1. Make sure your Netlify site is deployed with functions enabled
2. Check that the 'netlify/functions/teamgpt-proxy.js' file is properly included in your deployment
3. Verify your Netlify configuration (netlify.toml) includes the functions directory
4. Try visiting your-site/.netlify/functions/teamgpt-proxy in your browser to check if it returns a method not allowed error (which would indicate it exists but requires POST requests)`;
      }
      
      throw new Error(`Status: ${response.status}, Message: ${errorText}`);
    }
    
    const data = await response.json();
    console.log("Received response from proxy:", data);
    
    // Extract the response content
    if (data.choices && data.choices[0] && data.choices[0].message) {
      return data.choices[0].message.content;
    } else {
      console.error("Unexpected response format:", data);
      throw new Error("Unexpected response format from TeamGPT API");
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error("Error in callTeamGptApi:", errorMessage);
    
    // Check if the error is likely due to CORS or network issues
    if (
      errorMessage.includes("Failed to fetch") || 
      errorMessage.includes("NetworkError") || 
      errorMessage.includes("CORS")
    ) {
      return `I'm unable to connect to TeamGPT due to network issues.

If you're seeing this after deploying to Netlify, you need to:

1. Make sure your Netlify site is deployed with functions enabled
2. Check that the 'netlify/functions/teamgpt-proxy.js' file exists in your deployment
3. Verify the netlify.toml has the correct functions directory configuration
4. Try visiting your-site.netlify.app/.netlify/functions/teamgpt-proxy in your browser (should return a "Method Not Allowed" error which indicates the function exists)
5. Check if your browser console shows any CORS errors

If you're running locally, make sure to use the Netlify CLI with 'netlify dev' to test functions locally.`;
    }
    
    return `Sorry, the TeamGPT API request was not successful. Error details:\n\n${errorMessage}\n\nPlease check your API key and network connection.`;
  }
};
